// window.voxel debug API (UI-9). This is the Playwright/devtools contract: tests
// drive the game through it and assert via its getters. Changes here are
// breaking-change reviewed. Look direction is derived from yaw/pitch so mine/
// place work deterministically under tickOnce() without waiting for a render.
import { raycastVoxel, type RaycastHit } from '../sim/engine/raycast';
import type { ChildOpts } from './world/doors';
import { setWeather } from '../sim/engine/weather';
import { hashWorld } from '../sim/engine/worldhash';
import { REP, ATLAS } from '../sim/config';
import { Recorder, InstantRing } from '../sim/replay/recorder';
import type { SessionInit } from '../sim/replay/session';
import { ReplayPlayer, replayFinalHash } from '../sim/replay/player';
import {
  encodeReplay, decodeReplay, replayToJsonl, replayFromJsonl,
  type InputFrame, type Replay,
} from '../sim/replay/format';
import { addEffect } from '../sim/game/effects';
import { isEmpty, makeStack, INV } from '../sim/game/inventory';
import { fillChestLoot, chestContents } from '../sim/game/containers/chest';
import { furnaceContents } from '../sim/game/containers/furnace';
import { arrayContents } from '../sim/game/containers/array';
import { exportWorldFile } from './storage/export';
import { unpackSeed } from './render/textures/forge';
import { loadBundledFixture } from './earth/tiles';
import { decodeDemData } from '../sim/earth/tiles';
import type { GameMode } from '../sim/game/modes';
import type { GameAction } from './keybinds';
import type { StatCategory, CustomStat } from '../sim/game/stats';
import type { ClassroomSnapshot, Game, InputState } from './boot';
import type { Traits } from '../sim/heredity/genome';
import type { Notebook } from '../sim/heredity/notebook';
import type { LessonDef } from '../sim/registry/schemas';
import type { Trigger } from '../sim/game/advancements';
import type { QuizAnswer } from '../sim/education/quiz';
import type { LessonResult } from '../sim/education/results';
import type { RosterRow } from './ui/roster';

export interface VoxelApi {
  game: Game;
  player: Game['player'];
  getState(x: number, y: number, z: number): number;
  getBlock(x: number, y: number, z: number): string;
  /** Resolved block-state props at a cell (piston `extended`/`facing`, observer `powered`). */
  props(x: number, y: number, z: number): Readonly<Record<string, string>>;
  setBlock(x: number, y: number, z: number, block: number | string): void;
  tp(x: number, y: number, z: number): void;
  look(yaw: number, pitch: number): void;
  setInput(partial: Partial<InputState>): void;
  forceLock(v: boolean): void;
  tickOnce(n?: number): void;
  raycast(maxDist?: number): RaycastHit | null;
  mine(): boolean;
  place(block?: number | string): boolean;
  give(item: string, count?: number): void;
  command(text: string): string;
  // ── Inventory/survival introspection (M4 — E2E asserts through these) ──
  /** Snapshot of every inventory slot as `{name, count}` (empty → null). */
  inventory(): Array<{ name: string; count: number } | null>;
  /** Total count of `item` across the inventory. */
  countItem(item: string): number;
  /** First inventory slot index holding `item`, or -1. */
  slotOf(item: string): number;
  /** Select hotbar column 0..8. */
  select(n: number): void;
  /** Held item name ('' if the hand is empty). */
  heldItem(): string;
  openInventory(): void;
  openTable(): void;
  openCreative(): void;
  gameMode(): GameMode;
  setGameMode(mode: GameMode): void;
  // ── Entities/mobs (M5 — E2E asserts through these) ──
  /** Spawn a mob (MOB-1). Returns its entity id, or -1 for an unknown type. */
  spawnMob(type: string, x: number, y: number, z: number, opts?: { baby?: boolean; persistent?: boolean; variant?: number; tamed?: boolean }): number;
  /** The entity type of mob `id` (e.g. after a zombie→drowned conversion), or null. */
  mobType(id: number): string | null;
  /** Live mob count, optionally filtered by type. */
  mobCount(type?: string): number;
  /** Snapshot of every live mob. */
  mobs(): Array<{ id: number; type: string; x: number; y: number; z: number; health: number; baby: boolean; fuse: number; tamed: boolean; sitting: boolean; sheared: boolean; variant: number }>;
  /** Nearest live mob to the player (optionally by type), or null. */
  nearestMob(type?: string): { id: number; type: string; x: number; y: number; z: number; health: number; dist: number; tamed: boolean; sitting: boolean; sheared: boolean } | null;
  /** Melee-attack the mob under the crosshair (PLR-3). */
  attack(): boolean;
  /** Right-click interact with the mob under the crosshair — tame/sit/shear (MOB-2). */
  useEntity(): boolean;
  // ── Villager trading (VIL, M14.1 — E2E asserts through these) ──
  /** A villager's offers with live use counts + affordability. */
  villagerOffers(entityId: number): ReturnType<Game['villagerOffers']>;
  /** Execute trade `index` with the villager. Returns success. */
  doTrade(entityId: number, index: number): boolean;
  /** The villager's scheduled activity at the current world time (MB.7). */
  villagerActivity(entityId: number): ReturnType<Game['villagerActivity']>;
  /** The player's reputation with this villager (gossip weighted sum, MB.7). */
  villagerReputation(entityId: number): number;
  /** Grant a gossip event about the player to this villager (MB.7 test seam). */
  addVillagerGossip(entityId: number, type: Parameters<Game['addVillagerGossip']>[1], amount: number): void;
  /** Force one gossip share round (nearby villagers swap reputation). */
  shareVillagerGossip(): void;
  /** Force one gossip decay step (every ledger nudges toward zero). */
  decayVillagerGossip(): void;
  /** Mark a mob as under Weakness (gates a zombie-villager cure, MB.6). */
  weakenMob(entityId: number): boolean;
  /** Drive the real right-click interaction on a mob (feed/cure/trade/tame). */
  interactMob(entityId: number): boolean;
  /** Fast-forward an in-progress cure to complete on the next tick (MB.6). */
  finishCure(entityId: number): boolean;
  /** The player's powder-snow freeze counter in ticks (MB.1). */
  frozenTicks(): number;
  /** Honey level 0..5 of the beehive at (x,y,z), or -1 (MB.5). */
  hiveHoney(x: number, y: number, z: number): number;
  /** Simulate a bee's nectar delivery to the hive at (x,y,z) (MB.5). */
  pollinateHive(x: number, y: number, z: number): boolean;
  /** Make the frog eat a nearby slime/magma cube; returns the froglight name (MB.5). */
  frogEat(entityId: number): string;
  /** Make the chicken lay an egg now; true if a chicken was there (MB.3). */
  layEgg(entityId: number): boolean;
  /** Run one mob spawn cycle now; returns the number spawned (MB.5). */
  forceSpawnCycle(): number;
  /** Whether the villager is willing to breed (fed enough, MB.7). */
  villagerWilling(entityId: number): boolean;
  /** Run one villager-breeding pass; true if a baby was born (MB.7). */
  tryBreedVillagers(): boolean;
  /** The bed cell a villager paths to at night, assigning one now if needed (MB.7). */
  villagerBed(entityId: number): { x: number; y: number; z: number } | null;
  /** Give a jobless villager a nearby workstation's profession; new type or null (MB.7). */
  assignVillagerJob(entityId: number): string | null;
  /** Try to spawn a village iron golem now; true if summoned (MB.7). */
  trySpawnIronGolem(): boolean;
  /** Spawn a pillager patrol near the player; returns how many spawned (MB.6). */
  forcePatrol(): number;
  /** Ring the bell at (x,y,z): reveal nearby raiders; returns the count (MB.6). */
  ringBell(x: number, y: number, z: number): number;
  /** Have the evoker conjure vexes now; returns how many (MB.6). */
  summonVexes(entityId: number): number;
  /** Fire the dispenser at (x,y,z) once (exotic action or ejection); true if present (MB.7). */
  fireDispenser(x: number, y: number, z: number): boolean;
  /** Load a brewing stand's bottles/ingredient/fuel (MB.4). */
  loadBrewingStand(x: number, y: number, z: number, bottles: string[], ingredient: string, fuel: number): void;
  /** Fast-forward a brewing stand by one batch; true if it brewed (MB.4). */
  finishBrewing(x: number, y: number, z: number): boolean;
  /** The brewing stand's 3 bottle potion keys (MB.4). */
  brewingStandBottles(x: number, y: number, z: number): string[];
  /** The current status-effect screen wash {r,g,b,a} (MB.4). */
  effectTint(): { r: number; g: number; b: number; a: number };
  /** The render brightness floor from night vision (1 while active) (MB.4). */
  nightVisionFloor(): number;
  /** Write a sign's four text lines at (x,y,z) (MB.1). */
  setSignText(x: number, y: number, z: number, lines: string[]): void;
  /** Read a sign's four text lines at (x,y,z) (MB.1). */
  signText(x: number, y: number, z: number): string[];
  /** Whether the sign at (x,y,z) has glowing text (glow-ink applied) (M22). */
  signGlows(x: number, y: number, z: number): boolean;
  /** The lit board pixels the sign's text rasterizes to (MB.1). */
  signPixels(x: number, y: number, z: number): ReturnType<Game['signPixels']>;
  /** The sign board's pixel grid size. */
  signBoardSize(): ReturnType<Game['signBoardSize']>;
  /** Opaque pixel count of the sign's rendered board texture (MB.1). */
  signTextureLitCount(x: number, y: number, z: number): number;
  // ── Note blocks (MB.1) ──
  /** Current note (0..24) of the note block at (x,y,z). */
  noteBlockNote(x: number, y: number, z: number): number;
  /** Advance the note block's note by one (right-click) + play it; returns the payload. */
  cycleNoteBlock(x: number, y: number, z: number): ReturnType<Game['cycleNoteBlock']>;
  /** Play the note block at (x,y,z) without cycling (as a redstone pulse would). */
  playNoteBlock(x: number, y: number, z: number): ReturnType<Game['playNoteBlock']>;
  /** The most recent note-block play payload, or null. */
  lastNotePlayed(): ReturnType<Game['playNoteBlock']> | null;
  // ── Crafter (MB) ──
  /** Set a crafter grid slot (creates the block entity). */
  setCrafterSlot(x: number, y: number, z: number, i: number, item: string, count?: number): void;
  /** Toggle a crafter slot's disabled flag (empty slots only). */
  toggleCrafterSlot(x: number, y: number, z: number, i: number): void;
  /** The crafter grid contents at (x,y,z). */
  crafterSlots(x: number, y: number, z: number): ReturnType<Game['crafterSlots']>;
  /** Fire the crafter at (x,y,z) once (as a redstone pulse would). */
  fireCrafter(x: number, y: number, z: number): boolean;
  /** The most recent crafter output, or null. */
  lastCrafted(): Game['lastCrafted'];
  /** Round-trip all entities through save/reload (proves villager persistence). */
  reloadEntitiesRoundTrip(): void;
  // ── Raids (RAID, M14.2 — E2E asserts through these) ──
  /** Active-raid summary (status/wave/alive/raiderIds), or null. */
  raid(): ReturnType<Game['raidInfo']>;
  /** Force-start a raid at the nearest village (test convenience). */
  forceRaid(): boolean;
  /** Serialize the active raid's progress, or null (MB.6). */
  raidState(): ReturnType<Game['raidState']>;
  /** Rebuild the active raid mid-flight from a snapshot (MB.6). */
  restoreRaid(state: Parameters<Game['restoreRaid']>[0]): boolean;
  /** Damage a mob by id as if the player struck it (drives a raid to completion). */
  hurtMob(entityId: number, amount: number): boolean;
  // ── Combat gear (PLR-3, M17 — E2E asserts through these) ──
  /** Deal `amount` raw damage to the player through the real gear-mitigation path
   *  (armour points + EPF + totem save). `fromX/fromZ` set the knockback source. */
  hurtPlayer(amount: number, fromX?: number, fromZ?: number): void;
  /** Equip an item: armour → its column (helmet…boots), shield/totem → offhand. */
  equip(item: string): boolean;
  // ── Elytra glide (M21 — The End) ──
  /** Force-start an elytra glide (bypasses the jump edge). */
  glide(): void;
  /** Whether the player is currently gliding. */
  gliding(): boolean;
  /** Boost an active glide with a held firework rocket; true if one was consumed. */
  useFirework(): boolean;
  /** Force a fishing bite + reel, returning caught item names (MB.3). */
  fishCatch(): string[];
  // ── Cartography (UI-8, M18 — E2E asserts through these) ──
  /** Create a filled map at the player, select it on the hotbar; returns its id. */
  createFilledMap(scale?: number): number;
  /** Create a treasure map pinned to the nearest buried treasure; returns the mapId + target (M18). */
  createTreasureMap(scale?: number): { mapId: number; targetX: number | null; targetZ: number | null };
  /** mapId of a held/offhand `filled_map`, or -1. */
  heldMapId(): number;
  /** Number of maps in the level store. */
  mapCount(): number;
  /** Re-sample a filled map around the player; returns newly-filled pixel count. */
  refreshMap(mapId: number, radius?: number): number;
  /** [r,g,b,a] of pixel (u,v) on a filled map (a=0 ⇒ blank/unexplored). */
  mapPixel(mapId: number, u: number, v: number): [number, number, number, number] | null;
  /** Count of sampled (non-blank) pixels on a filled map. */
  mapMappedCount(mapId: number): number;
  /** The player pointer on a map (pixel u,v + yaw + whether on-map), or null. */
  mapMarker(mapId: number): { u: number; v: number; rot: number; inBounds: boolean } | null;
  /** A fixed target pin (treasure "X") on a map for a world point, or null. */
  mapTarget(mapId: number, tx: number, tz: number): { u: number; v: number; rot: number; inBounds: boolean } | null;
  // ── Amethyst geodes (GEN, M19 — E2E asserts through this) ──
  /** Force one budding_amethyst growth step at (x,y,z) on face `dirIndex`
   *  (default 4 = up). Returns true if a bud sprouted/advanced. */
  growAmethyst(x: number, y: number, z: number, dirIndex?: number): boolean;
  // ── Sculk (Deep Dark, M22 — E2E asserts through this) ──
  /** Bloom sculk from a death at (x,y,z) with `charge` XP. Returns blocks
   *  converted (=== charge spent). Conservation: spent ≤ charge. */
  bloomSculk(x: number, y: number, z: number, charge: number): number;
  // ── Beacon (M20.1 — E2E asserts through this) ──
  /** Pyramid tier (0..4) beneath the beacon at (x,y,z). */
  beaconTier(x: number, y: number, z: number): number;
  /** Set the beacon at (x,y,z)'s chosen primary (+ optional secondary) effect. */
  setBeaconEffect(x: number, y: number, z: number, primary: string, secondary?: string | null): void;
  /** Force one beacon application; returns the effects it applied. */
  pulseBeacon(x: number, y: number, z: number): { id: string; amplifier: number }[];
  /** Ignite a built wither summon frame centred at (x,y,z); true if a wither spawned. */
  summonWither(x: number, y: number, z: number): boolean;
  // ── Conduit (M18.1 — E2E asserts through this) ──
  /** Prismarine frame-block count around the conduit at (x,y,z). */
  conduitFrame(x: number, y: number, z: number): number;
  /** Force one conduit application; returns its frame/range/powered state. */
  pulseConduit(x: number, y: number, z: number): { frame: number; range: number; powered: boolean };
  // ── Warden + sculk shrieker (M22.1 — E2E asserts through this) ──
  /** Trigger a sculk shrieker; returns the new warning level + whether a warden spawned. */
  shriek(x: number, y: number, z: number): { level: number; summoned: boolean };
  /** Make warden `id` fire its armour-piercing sonic boom at the player; damage dealt. */
  wardenSonicBoom(id: number): number;
  // ── Ender dragon (M21.1 — E2E asserts through this) ──
  /** Intact end crystals shielding the ender dragon `id`. */
  dragonCrystals(id: number): number;
  /** The ender dragon `id`'s current flight phase, or null if not a dragon. */
  dragonPhase(id: number): string | null;
  // ── Trial chambers (M22.1 — E2E asserts through this) ──
  /** Total mobs a trial spawner releases for the current player count. */
  trialWave(): number;
  /** Simultaneous-alive cap for the current player count. */
  trialCap(): number;
  /** Force the trial spawner at (x,y,z) to complete its wave so it ejects the key. */
  forceTrialEject(x: number, y: number, z: number): void;
  // ── Dripstone (M19.1 — E2E asserts through this) ──
  /** Force one deterministic dripstone growth step at the down-tip (x,y,z). */
  growDripstone(x: number, y: number, z: number): boolean;
  // ── Respawn anchor (M20.1 — E2E asserts through this) ──
  /** Right-click the respawn anchor at (x,y,z); returns the action taken. */
  useAnchor(x: number, y: number, z: number): string;
  // ── Ocean fauna (M18.1 — E2E asserts through this) ──
  /** Apply dolphin-grace / pufferfish-poison to the player at current positions. */
  pulseOceanFauna(): void;
  /** Fully charge + fire nearby guardian beams and apply the elder aura now (M18.1). */
  pulseGuardians(): void;
  /** Force the hoglin `id` past its overworld zombify timer → zoglin (M17.1). */
  zombifyHoglin(id: number): boolean;
  /** Run one turtle-egg random tick at (x,y,z): cracks, or hatches at night on sand (M18.1). */
  turtleEggTick(x: number, y: number, z: number): void;
  /** Emit a vibration at (x,y,z) → wake nearby sculk sensors + resolve redstone (M22.1). */
  sculkVibrate(x: number, y: number, z: number): void;
  /** Detonate a wind-charge burst at (x,y,z): knock entities back + toggle activatable blocks (M22.1). */
  windCharge(x: number, y: number, z: number): void;
  /** Brush the suspicious block at (x,y,z) one stage; returns the revealed item name once dug out, else '' (M41). */
  brushBlock(x: number, y: number, z: number): string;
  /** Force sniffer `id` to dig now; returns the unearthed seed name, or '' (M19). */
  snifferDig(id: number): string;
  /** Make camel `id` dash (if off cooldown); returns true if it dashed (M19). */
  camelDash(id: number): boolean;
  /** Grow tadpole `id` into a frog now; returns the froglight its variant would make, or '' (M19). */
  growTadpole(id: number): string;
  // ── Copper oxidation (MB.1 — E2E asserts through this) ──
  /** Force one deterministic copper oxidation step at (x,y,z); returns the new
   *  block name, or null if it can't oxidize (waxed / already oxidized). */
  oxidizeCopper(x: number, y: number, z: number): string | null;
  /** Occupied cooking-slot count of the campfire at (x,y,z). */
  campfireCount(x: number, y: number, z: number): number;
  /** Fast-forward the campfire at (x,y,z) to completion; returns slots cooked. */
  cookCampfire(x: number, y: number, z: number): number;
  /** Run one rain-fill step on the cauldron at (x,y,z); returns its water level. */
  rainCauldron(x: number, y: number, z: number): number;
  // ── Farming growth (MB.2 — E2E asserts through this) ──
  /** Force one deterministic growth step for the plant at (x,y,z). */
  growPlant(x: number, y: number, z: number): boolean;
  // ── Snow layers (MB.1 — E2E asserts through these) ──
  /** Run the real random-tick handler on the cell (snow melt/accumulate/coat). */
  randomTick(x: number, y: number, z: number): void;
  /** Place the currently-held item at (x,y,z) via the real placement path. */
  placeHeld(x: number, y: number, z: number): boolean;
  // ── Redstone (RED, M15.1 — E2E asserts through this) ──
  /** Redstone state at a cell (power/lit/powered), or null. */
  redstone(x: number, y: number, z: number): ReturnType<Game['redstoneAt']>;
  // ── Block entities / containers (M6 — E2E asserts through these) ──
  /** Right-click use the block at (x,y,z) — open a container / toggle a door. */
  useBlock(x: number, y: number, z: number): boolean;
  /** Block-entity type at (x,y,z), or null. */
  blockEntityType(x: number, y: number, z: number): string | null;
  /** Set a container slot (chest 0..26 / furnace 0=in,1=fuel,2=out). Creates the
   *  block entity if the block carries one. Returns true on success. */
  putContainer(x: number, y: number, z: number, slot: number, item: string, count: number): boolean;
  /** Every non-empty slot of the container at (x,y,z) as `{name,count}`; a filled
   *  map also reports the `mapId` its stack references (M18). */
  containerItems(x: number, y: number, z: number): Array<{ name: string; count: number; mapId?: number }>;
  /** Realise a structure chest's pending loot (as opening it would). Returns the
   *  number of non-empty slots afterward. */
  realizeChest(x: number, y: number, z: number): number;
  /** Give the chest at (x,y,z) a pending structure loot table + roll seed, as
   *  worldgen does — so `realizeChest` then walks the real loot path (M18). */
  seedLootChest(x: number, y: number, z: number, table: string, seed?: number): boolean;
  /** The world target pinned on a map (the treasure "X"), or null (M18). */
  mapPin(mapId: number): { x: number; z: number } | null;
  /** Nearest structure of `type` to the player, in the ACTIVE dimension's set
   *  (`/locate` without the chat round trip). Null if none / not applicable. */
  locate(type: string): { x: number; z: number } | null;
  /** The mob a spawner block entity at (x,y,z) is set to, or null. */
  spawnerMob(x: number, y: number, z: number): string | null;
  /** Emit a sculk vibration at (x,y,z) from `source` (0 = the player, else an
   *  entity id) — the same bus player steps, mob steps and block edits use (M22.1). */
  vibrate(event: string, x: number, y: number, z: number, source?: number): void;
  /** A mount's rolled attributes + live ride state (M19): speed/jump multipliers,
   *  the horse's jump charge, and a camel's dash cooldown. Null if not a mount. */
  mountState(id: number): { speedMul: number; jumpMul: number; jumpCharge: number; dashCd: number; saddled: boolean } | null;
  /** A warden's anger toward a suspect, and what it is currently hunting (M22.1). */
  wardenAnger(wardenId: number, suspect: number): number;
  wardenQuarry(wardenId: number): { suspect: number; anger: number; hunting: boolean } | null;
  /** Close any open container/inventory window (dispatches Escape). */
  closeUI(): void;
  /** Live XP-orb count (PLR-5). */
  readonly orbCount: number;
  /** Live projectile (arrow) count (MOB-5). */
  readonly projectileCount: number;
  readonly health: number;
  readonly hunger: number;
  readonly xpLevel: number;
  readonly xpTotal: number;
  readonly flying: boolean;
  /** Live loose-item-entity count (E-17). */
  readonly dropCount: number;
  /** Live falling-block count (sand/gravel/concrete powder, M10.1). */
  readonly fallingCount: number;
  /** Live primed-TNT count (RED-8, M15.2.2). */
  readonly primedTntCount: number;
  /** Prime the TNT block at (x,y,z) directly (debug/E2E). True if it was TNT. */
  ignitePrimedTnt(x: number, y: number, z: number): boolean;
  /** Live minecart count (RED-5/6, M16.2). */
  readonly minecartCount: number;
  /** Spawn a minecart (any variant) on the rail at (x,y,z) heading `dir` (debug/E2E). */
  spawnMinecart(x: number, y: number, z: number, dir?: 'north' | 'east' | 'south' | 'west', speed?: number, kind?: string): void;
  /** Fuel furnace-cart `i` for `ticks` of self-propulsion (debug/E2E). */
  fuelMinecart(i: number, ticks?: number): void;
  /** Spawn a loose item-drop of `item` at (x,y,z) (debug/E2E). */
  dropItem(item: string, x: number, y: number, z: number, count?: number): void;
  /** Live boat count (RED-6). */
  readonly boatCount: number;
  /** Spawn a boat (or 'chest_boat') at (x,y,z) heading `yaw` (debug/E2E). Returns its id. */
  spawnBoat(x: number, y: number, z: number, yaw?: number, kind?: string): number;
  /** Feed steering to boat id (debug/E2E; passenger binding is excluded). */
  setBoatInput(id: number, input: { forward?: boolean; back?: boolean; left?: boolean; right?: boolean }): void;
  /** Read boat `i`: position, heading, whether it floats, variant, item count — or null. */
  boatAt(i: number): { id: number; x: number; y: number; z: number; yaw: number; onWater: boolean; kind: string; items: number } | null;
  /** Mount boat `id` / dismount / query the ridden boat id (passenger binding, B4). */
  mountBoat(id: number): boolean;
  dismountBoat(): void;
  ridingBoatId(): number | null;
  /** Mount/dismount/query/saddle a land mob the player rides (M19). */
  mountEntity(id: number): boolean;
  dismountMount(): void;
  ridingMount(): number | null;
  saddleMount(id: number): boolean;
  // ── Aviation (M32, AVI-2 — E2E flies the pattern through these) ──
  /** Live aircraft count (only ever >0 on an aviation-enabled Atlas world). */
  readonly aircraftCount: number;
  /** Climb into the parked aircraft (spawns one on the runway); false if aviation off. */
  enterAircraft(): boolean;
  /** Leave the cockpit, stepping onto the ground beside the aircraft. */
  exitAircraft(): void;
  /** The flown aircraft's full state + derived instruments, or null if not flying. */
  aircraft(): {
    x: number; y: number; z: number; yaw: number; pitch: number; roll: number;
    throttle: number; onGround: boolean; airborne: boolean; crashed: boolean;
    heading: number; airspeed: number; altitude: number;
  } | null;
  /** Override the aircraft controls each tick (pitch/roll/yaw −1..1, throttle up/down,
   *  brake); pass null to hand control back to the pilot's keys (AVI-3 autopilot uses this). */
  setAircraftInput(input: { pitch?: number; roll?: number; yaw?: number; throttleUp?: boolean; throttleDown?: boolean; brake?: boolean } | null): void;
  /** Engage the PID autopilot (AVI-3) to hold an altitude (+ optional heading, radians)
   *  at a cruise throttle; pass null to disengage. */
  setAutopilot(targets: { altitude: number; heading?: number | null; throttle?: number } | null): void;
  /** Whether the autopilot is currently flying. */
  autopilotEngaged(): boolean;
  /** SAR / ground-team trainer (M36): deploy/remove/clear ELT beacons, list them,
   *  read the homed-beacon signal + the local search objective, toggle the HUD. */
  readonly sar: {
    deploy(x: number, y: number, z: number): number;
    remove(id: number): boolean;
    clear(): void;
    beacons(): { id: number; x: number; y: number; z: number }[];
    signal(x: number, y: number, z: number): { dist: number | null; rssi: number; bars: number };
    bearing(x: number, y: number, z: number, yaw: number): { relBearing: number | null; led: number | null };
    objective(searcher?: string): { dwell: number; found: boolean };
    hud(v?: boolean): boolean;
  };
  /** SAR helicopter (M36.3, SAR-5): spawn/enter/exit/board (returns seat success),
   *  input override, and read the piloted machine's state. */
  readonly heli: {
    readonly count: number;
    spawn(x: number, y: number, z: number, yaw?: number): number;
    enter(id: number): boolean;
    exit(): void;
    board(id: number): boolean;
    input(i: { collectiveUp?: boolean; collectiveDown?: boolean; pitch?: number; roll?: number; yaw?: number } | null): void;
    at(i: number): { id: number; x: number; y: number; z: number; yaw: number; vy: number; onGround: boolean; crashed: boolean; altitude: number } | null;
    riders(id: number): number;
    piloted(): number | null;
  };
  /** SAR van (M36.3, SAR-5): spawn/enter/exit/board, input override, read state. */
  readonly van: {
    readonly count: number;
    spawn(x: number, y: number, z: number, yaw?: number): number;
    enter(id: number): boolean;
    exit(): void;
    board(id: number): boolean;
    input(i: { throttle?: boolean; brake?: boolean; reverse?: boolean; steer?: number } | null): void;
    at(i: number): { id: number; x: number; y: number; z: number; yaw: number; speed: number } | null;
    riders(id: number): number;
    piloted(): number | null;
  };
  /** Classroom / guided-build (M37, CLS-1..3): place a ghost blueprint / a course,
   *  read the live placed-vs-plan diff, toggle the heat overlay, list completed
   *  lessons, and read the instructor's aim origin. */
  readonly classroom: {
    aim(): { x: number; y: number; z: number };
    place(dsl: string, x: number, y: number, z: number, rot?: number): { ok: boolean; cells: number; error?: string };
    course(entries: { dsl: string; x: number; y: number; z: number; rot?: number }[]): { ok: boolean; cells: number; error?: string };
    clear(): void;
    state(): ClassroomSnapshot | null;
    heat(v?: boolean): boolean;
    done(): readonly string[];
  };
  /** Read minecart `i` (debug/E2E): position, rail cell, heading, speed, variant,
   *  fuse, and stored-item count — or null. */
  minecartAt(i: number): { x: number; y: number; z: number; cx: number; cy: number; cz: number; dir: string; speed: number; kind: string; fuse: number; items: number } | null;
  /** Whether the death screen is showing (PLR-10). */
  readonly dead: boolean;
  // ── Fluids (M9.1 — E2E asserts through these) ──
  /** Flow level (0 source .. 7) of the fluid at a cell; 0 for non-fluids. */
  fluidLevel(x: number, y: number, z: number): number;
  /** Use the held bucket on the fluid/block under the crosshair (M9.1). */
  useBucket(): boolean;
  /** Right-click the held item (throw a splash potion, cast a rod, …) (MB.4). */
  useHeldItem(): boolean;
  /** Right-click use of a held field notebook — record a genetic crop under the
   *  crosshair or open the screen (HER-7, §28.11). False if not holding one. */
  useFieldNotebook(): boolean;
  // ── Farming (M11.1 — E2E asserts through these) ──
  /** Use the held item on a block (hoe/seed/bonemeal); `ny` defaults to 1 (top). */
  useItemOnBlock(x: number, y: number, z: number, ny?: number): boolean;
  /** Use the held item on a specific clicked FACE (full normal) — e.g. a torch on
   *  a wall (MB.1). */
  useItemOnFace(x: number, y: number, z: number, nx: number, ny: number, nz: number): boolean;
  /** A block state property value at (x,y,z), or undefined. */
  prop(x: number, y: number, z: number, name: string): string | undefined;
  // ── Enchanting (M12.1 — E2E asserts through these) ──
  /** Enchant offers for the held tool at a table (PLR-6). */
  enchantOffers(x: number, y: number, z: number): Array<{ level: number; lapisCost: number; enchants: Array<{ id: string; lvl: number }> }>;
  /** Apply enchant slot `slot` to the held tool at the table. */
  enchant(x: number, y: number, z: number, slot: number): boolean;
  /** Enchants on the held item. */
  heldEnchants(): ReadonlyArray<{ id: string; lvl: number }>;
  // ── Status effects (M12.2 — E2E asserts through these) ──
  /** Active status effects on the player (PLR-8). */
  effects(): Array<{ id: string; amplifier: number; ticks: number }>;
  /** Apply a status effect directly (test convenience). */
  giveEffect(id: string, amplifier?: number, ticks?: number): void;
  /** Throw a splash potion straight down onto the player so it shatters on them
   *  (MB.6). Exercises the real projectile → shatter → AoE-apply path. */
  throwSplash(effect?: string, amplifier?: number, seconds?: number): void;
  // ── Weather + sleep (M9.2 — E2E asserts through these) ──
  readonly raining: boolean;
  readonly thundering: boolean;
  /** Ticks since the player last slept (PLR-10 phantom counter). */
  readonly timeSinceRest: number;
  /** Set the weather directly (skips the /weather parser in tests). */
  setWeather(kind: 'clear' | 'rain' | 'thunder', durationSeconds?: number): void;
  /** Sky-light level (0..15) at a cell (§5.2). */
  skyLight(x: number, y: number, z: number): number;
  /** Block-light level (0..15) at a cell (§5.2). */
  blockLight(x: number, y: number, z: number): number;
  // ── Audio (AUD-1, M23 — E2E asserts on the event log, not on audible output) ──
  /** Break the block at (x,y,z) through the host (fires the material dig voice). */
  mineAt(x: number, y: number, z: number): void;
  /** Place the held block at (x,y,z) through the host (fires the material place voice). */
  placeHeldAt(x: number, y: number, z: number): boolean;
  /** Play a sound event by id at an optional world position. */
  playSound(id: string, x?: number, y?: number, z?: number, pitch?: number): void;
  /** The logged audio events (id/bus/position/pitch), oldest first. */
  audioEvents(): ReadonlyArray<{ bus: string; id: string; x: number; y: number; z: number; pitch: number; seq: number }>;
  /** The most recent audio event's id, or '' if none. */
  lastAudioId(): string;
  /** Clear the audio-event log. */
  clearAudio(): void;
  /** Resume the WebAudio context (autoplay policy; a no-op without one). */
  resumeAudio(): void;
  /** Set an audio bus volume 0..1 (master|sound|music). */
  setAudioVolume(bus: 'master' | 'sound' | 'music', v: number): void;
  /** Current audio bus volumes. */
  audioVolumes(): { master: number; sound: number; music: number };
  // ── Jukebox / music (AUD-4) ──
  /** Insert a disc into the jukebox at (x,y,z) (plays it). False if invalid/occupied. */
  insertDisc(x: number, y: number, z: number, disc: string): boolean;
  /** The disc spinning in the jukebox at (x,y,z), or null. */
  jukeboxDisc(x: number, y: number, z: number): string | null;
  /** The comparator index (0..15) the jukebox at (x,y,z) outputs. */
  discComparator(x: number, y: number, z: number): number;
  /** The current ambient music context, or null before the first frame. */
  musicContext(): string | null;
  /** Toggle the subtitles caption overlay (M23 accessibility). */
  setSubtitles(on: boolean): void;
  /** Active subtitle caption lines, oldest first. */
  subtitleLines(): string[];
  // ── Advancements (M24) ──
  /** Completed advancement ids. */
  advancementsDone(): string[];
  /** Titles of advancement toasts shown so far. */
  advancementToasts(): string[];
  // ── Keybinds (M24) ──
  /** Rebind an action to a key code (clears conflicts), as the options UI does. */
  setKeybind(action: GameAction, code: string): void;
  /** The key code currently bound to an action ('' if unbound). */
  keybindFor(action: GameAction): string;
  // ── Statistics (M24) ──
  /** A per-id category tally (mined/used/picked_up/killed…). */
  statCount(category: StatCategory, id: string): number;
  /** A general custom total (play_time/mob_kills/deaths…). */
  statCustom(stat: CustomStat): number;
  /** Open the statistics screen. */
  openStats(): void;
  /** Capture the current frame → a PNG data URL (also downloads it). */
  screenshot(): string;
  /** How many screenshots have been taken this session. */
  screenshotCount(): number;
  /** Content export (tools/export-images.ts): the procedural textures rendered
   *  to PNG data URLs. This project ships no image files — every texture is
   *  painted in code — so exporting them means reading them back out here. */
  iconDataUrl(item: string): string;
  atlasDataUrl(): string;
  allItemNames(): string[];
  /** F3 debug overlay states + toggles (M24). */
  debugOverlayOn(): boolean;
  chunkBordersOn(): boolean;
  hitboxesOn(): boolean;
  /** Recipe book (M24): how many recipes are unlocked + whether a given one is. */
  recipeUnlockedCount(): number;
  isRecipeUnlocked(name: string): boolean;
  // ── Dimensions / portals (M7 — E2E asserts through these) ──
  /** Active dimension id ('overworld' | 'nether' | 'end'). */
  readonly dimension: string;
  /** True when the Atlas world came from the fallback synthesizer (ATL-2 badge). */
  readonly simulated: boolean;
  /** Ignite a nether portal at an interior air cell (DIM-2). */
  ignitePortal(x: number, y: number, z: number): boolean;
  /** Whether a nether portal block occupies (x,y,z). */
  isPortal(x: number, y: number, z: number): boolean;
  /** Mini-map radar radius in blocks (UI-8); cycle with `cycleMinimapZoom`. */
  readonly minimapRadius: number;
  cycleMinimapZoom(): number;
  /** Distinct minimap colour classes ('green'|'gray'|'brown'|'blue'|'white') the
   *  radar shows around the player right now — proxies the rendered pixels (UI-8). */
  minimapClasses(): string[];
  /** Set the world time-of-day directly (skips the /time parser in tests). */
  setTime(t: number): void;
  /** Current sky-light daylight scale (0..1) — proxy for day/night state. Reflects
   *  the real-solar override on Atlas worlds (SOL-1). */
  readonly daylight: number;
  /** Sun elevation in radians above the horizon (SOL-1); real solar on Atlas worlds,
   *  0 on the generic cycle. */
  readonly sunElevation: number;
  /** True when the sky is driven by real solar position (an Atlas world, SOL-1). */
  readonly skyReal: boolean;
  /** Persist the world now (autosave otherwise runs every 30s). */
  save(): Promise<void>;
  /** Export the current world to `.tfworld` JSONL — the browser→host flow (M29). */
  exportWorld(): Promise<string>;
  readonly fps: number;
  /** Loaded chunk count — proxy for engine memory (ENG-21 eviction). */
  readonly chunkCount: number;
  /** Worst render-frame ms since the last resetFrameStats() (perf gate). */
  readonly worstFrameMs: number;
  /** Total geometry uploads — increments synchronously on an edit remesh. */
  readonly meshRebuilds: number;
  resetFrameStats(): void;
  // ── Deterministic replay (M33, REP-1..5 — E2E asserts through these) ──
  replay: VoxelReplayApi;
  // ── Texture Forge (M34, FRG-1/2 — E2E asserts through these) ──
  forge: ForgeDebugApi;
  // ── Script Blocks (M35, SCR-4 — E2E asserts through these) ──
  script: ScriptDebugApi;
  // ── EarthTiles /v1 client (M39, TIL-1..5 — E2E asserts the default loads offline) ──
  earth: EarthDebugApi;
  // ── Reference overlays (M40.4, OVL-1..3 — E2E asserts add/move/persist + OVL-3 parity) ──
  overlay: OverlayDebugApi;
  // ── World doors (M41, DOR-1..5 — E2E asserts traversal/carry/TARDIS/resize/escape) ──
  door: DoorDebugApi;
  // ── Heredity (Part IX, ARCH §28.16 — E2E asserts through these) ──
  heredity: HeredityDebugApi;
  // ── Education & Content (Part X, ARCH §29.17 — E2E asserts through these) ──
  edu: EduDebugApi;
  // ── Co-working (Part XI, ARCH §30.12 — E2E asserts through these) ──
  cow: CowDebugApi;
  // ── Streaming/perf (worker pools + speed-aware LOD — E2E asserts through this) ──
  streaming: StreamingDebugApi;
  /** Multiplayer state (NET-2), present only in a `?server=` boot. */
  net?: NetDebugApi;
}

/** The `voxel.earth` surface (M39): load the BUNDLED KHFD tile set through the real
 *  contract client + sim decode, with ZERO network (ERT-6). The E2E asserts the
 *  summary + that no tile request left the page. */
export interface EarthDebugApi {
  /** Load + validate the bundled default (KHFD) tile set offline (TIL-1..3). */
  loadDefault(): {
    source: string;
    contractMajor: number;
    demTiles: number;
    vectorTiles: number;
    demSource: string;
    demW: number;
    /** A decoded elevation sample (m) from the middle of the first DEM tile. */
    sampleElev: number;
    roads: number;
    aeroway: number;
    attribution: string[];
  };
}

/** The `voxel.overlay` surface (M40.4): drive the Earth reference-overlay controller —
 *  upload, align, persist — and read the sim world-hash to prove OVL-3 (an overlay
 *  changes zero sim bytes). No-ops on non-Earth worlds. */
export interface OverlayDebugApi {
  /** True on Earth worlds (overlays are Earth-only in v1). */
  enabled(): boolean;
  /** Upload a PNG/JPEG data URL at the player position (NW corner); rejects per E-40. */
  add(dataUrl: string, pxW: number, pxH: number): Promise<{ ok: boolean; reason?: string; id?: string }>;
  list(): Array<{ overlayId: string; anchor: { x: number; y: number; z: number }; widthBlocks: number; opacity: number; hidden: boolean }>;
  count(): number;
  nudge(id: string, dx: number, dy: number, dz: number): void;
  setOpacity(id: string, opacity: number): void;
  toggleHidden(id: string): void;
  remove(id: string): void;
  /** Sim world-hash (OVL-3): identical whether or not an overlay is present. */
  worldHash(): string;
  /** Imagery bake (OVL-4, M43): freeze the aligned overlays (+ any NAIP tiles) into the
   *  ground as pinned generator input — navigates (reloads the world). Un-bake removes it. */
  bake(): Promise<void>;
  unbake(): Promise<void>;
  /** Whether this world has a pinned imagery bake (drives the panel + badge). */
  baked(): boolean;
}

/** The `voxel.door` surface (M41): drive the local world graph + world-session-swap
 *  traversal — read a door's edge data, create a bounded child world through an unbound
 *  door, traverse (which navigates to `?world=<targetId>`), resize under refuse-shrink,
 *  and the escape hatch. The E2E drives these instead of the click menu. */
export interface DoorDebugApi {
  /** The door's edge data at (x,y,z), or null if there's no world door there. */
  at(x: number, y: number, z: number): { doorId: string; targetWorldId: string; targetDoorId: string } | null;
  /** Create + bind a bounded child world through the door; returns the new world id.
   *  `public` (server worlds, M42) makes it private when false → ACL denial (E-37). */
  createChild(x: number, y: number, z: number, opts: { worldType: string; size?: number; name?: string; public?: boolean }): Promise<string>;
  /** Traverse the bound door (a world-session swap → navigates away). False if unbound. */
  enter(x: number, y: number, z: number): Promise<boolean>;
  /** Resize the door's bound target world (DOR-4); grow always, shrink refused with blockers. */
  resize(x: number, y: number, z: number, size: number): Promise<{ ok: boolean; size: number; blockers?: { kind: string; x: number; z: number }[] }>;
  /** The current world's id (arrival / TARDIS assertions). Net mode namespaces it `net:`. */
  worldId(): string;
  /** Net (M42, DOR-2): the return door the player arrived at, so a test can point back
   *  through it. Null in solo (or before any traversal). */
  arrivalDoor(): { x: number; y: number; z: number } | null;
  /** DOR-5 escape hatch: flush + return to the highest reachable home world's spawn. */
  returnHome(): Promise<void>;
  /** Local storage read-out (STO-1/3): per-world bytes + total vs quota. */
  usage(): Promise<{ worlds: { id: string; name: string; bytes: number }[]; totalBytes: number; quotaBytes: number; fraction: number; warn: boolean }>;
  /** Delete a world from the save (test helper — drives the E-34 inert-door path). */
  deleteWorld(id: string): Promise<void>;
}

/** The `voxel.streaming` surface: observe the map-loading pipeline — worker-pool
 *  sizes, the current surface-only mesh LOD (0 = full detail; >0 while flying high
 *  & fast), the loaded chunk-mesh count, and the smoothed streaming heading. */
export interface StreamingDebugApi {
  /** Current mesh LOD depth (0 = full detail). */
  lod(): number;
  /** The adaptive render-distance cap (≤ the user's setting; shrinks when fps sags). */
  renderCap(): number;
  /** Number of mesh-worker threads in the pool. */
  meshWorkers(): number;
  /** Number of gen-worker threads in the pool. */
  genWorkers(): number;
  /** Loaded chunk meshes (0 would mean the terrain vanished). */
  loadedChunks(): number;
  /** Smoothed ground heading driving prefetch (blocks/tick). */
  heading(): { vx: number; vz: number };
}

/** The `voxel.script` surface: drive a script block's source/run and its autopilot
 *  preset without the editor GUI (SCR-4). A cell with a `script_block` but no block
 *  entity yet gets one created on first use. */
export interface ScriptDebugApi {
  /** The stored source at (x,y,z), or null if there's no script block. */
  source(x: number, y: number, z: number): string | null;
  /** Save a source (compiles-checks it); returns the status. */
  set(x: number, y: number, z: number, source: string): { ok: boolean; error?: string };
  /** Dispatch the script once (default event `redstone`); returns a result message. */
  run(x: number, y: number, z: number, event?: string): { ok: boolean; message: string };
  /** Load an editable autopilot preset (AVI-3). */
  setAutopilot(x: number, y: number, z: number, preset: { altitude: number; heading: number | null; kp: number; ki: number; kd: number }): void;
  /** Engage the block's autopilot preset now; false if not flying. */
  engage(x: number, y: number, z: number): boolean;
}

/** The `voxel.heredity` surface (ARCH §28.16). Every member exists from M48.3 so the
 *  E2E contract is one stable shape; a member whose milestone has not landed yet throws
 *  `not yet (<milestone>)` rather than returning a plausible-looking value. */
export interface HeredityDebugApi {
  /** The `heredity` gamerule; pass a boolean to set it (as `/gamerule heredity` does). */
  rule(on?: boolean): boolean;
  /** The `heredityPace` gamerule (1..4; junk reads 1); pass a number to set it. */
  pace(n?: number): number;
  genome(id: number): string | null;
  setGenome(id: number, b64: string): boolean;
  traits(id: number): Traits | null;
  breed(a: number, b: number): number | null;
  ageTo(id: number, ticks: number): void;
  name(id: number): string | null;
  setName(id: number, text: string): boolean;
  cropGenome(x: number, y: number, z: number): string | null;
  plantGenetic(x: number, y: number, z: number, crop: string, b64?: string): boolean;
  harvest(x: number, y: number, z: number): { produce: number; seeds: string[] };
  record(id: number): boolean;
  recordCrop(x: number, y: number, z: number): boolean;
  notebook(): Notebook;
  overlayCount(): number;
  entityTint(id: number, part: string): string | null;
  /** Whether a rig part is visible (the chicken's comb) — beyond §28.16's list, because
   *  "the comb part is hidden" is otherwise unobservable from a test (D-TF-246). */
  entityPartVisible(id: number, part: string): boolean | null;
  entityScale(id: number): number | null;
  /** A mob's rendered nametag text — beyond §28.16, so "a nametag sprite exists" is
   *  observable from a test (D-TF-249). */
  entityNametag(id: number): string | null;
  lastActionBar(): string;
}

/** The `voxel.edu` surface (ARCH §29.17). Every member exists from M51.6 so the E2E
 *  contract is one stable shape, same discipline as `HeredityDebugApi`: a member
 *  whose milestone has not landed yet throws `not yet (<milestone>)`. `lessons` and
 *  `activeLesson` are real from M51.6 (the create-screen round-trip needs them);
 *  `fire`/`submitQuiz`/`result` land at M51.7; the instructor-only members
 *  (`raiseHand`/`frozen`/`roster`/`exportJson`) at M52. `lastActionBar` is the
 *  §28.16 surface re-exported, not reimplemented (D-TF-191). */
export interface EduDebugApi {
  lessons(): readonly LessonDef[];
  startLesson(name: string): boolean;
  stopLesson(): void;
  activeLesson(): string | null;
  result(sub?: string): LessonResult | null;
  /** Inject a trigger directly (solo test seam — every objective kind is
   *  unit-drivable without playing it out, §29.15). */
  fire(t: Trigger): string[];
  readInstrument(item: string, x?: number, y?: number, z?: number): string;
  submitQuiz(answers: readonly QuizAnswer[]): { score: number; n: number };
  raiseHand(up: boolean): void;
  frozen(): boolean;
  roster(): RosterRow[];
  exportJson(): string;
  lastActionBar(): string;
}

/** M55–M57's debug/E2E surface (ARCH §30.12): the three block entities' data,
 *  presence status, and (M57) the share lifecycle — a `fakeShare` seam so CI can
 *  drive share → watch → paint → stop with no display capture and no TURN. */
export interface CowDebugApi {
  status(v?: string): string;
  noteText(x: number, y: number, z: number): string | null;
  setNote(x: number, y: number, z: number, text: string): boolean;
  board(x: number, y: number, z: number): Array<{ text: string; done: boolean }> | null;
  toggleTask(x: number, y: number, z: number, i: number): boolean;
  timer(x: number, y: number, z: number): { endTick: number | null; preset: string } | null;
  timerFace(x: number, y: number, z: number): string | null;
  startTimer(x: number, y: number, z: number, preset: string): boolean;
  screenState(x: number, y: number, z: number): { live: boolean; sharer: number; viewers: string[] } | null;
  canShare(): boolean;
  viewers(x: number, y: number, z: number): string[];
  /** Test-only extension: per-viewer connection state (§30.6's viewer-list
   *  wording — "name + connecting|live|relayed|failed"). */
  viewerStates(x: number, y: number, z: number): Array<{ id: string; state: string }>;
  /** Test-only canvas `captureStream` share — M57. */
  fakeShare(x: number, y: number, z: number): boolean;
  /** Test-only (E-64 seam): force this client's own watching peer to fail. */
  forceWatchFail(): void;
  signText(x: number, y: number, z: number): string[];
  lastActionBar(): string;
}

/** The `voxel.forge` surface: open the Texture Forge, import + apply a seed string,
 *  read the current skin's seed, and sample the re-skinned atlas tile (FRG-1/2). */
export interface ForgeDebugApi {
  /** Open the Texture Forge screen. */
  open(): void;
  /** Import a seed string and re-skin the forged_block variant (throws on a bad or
   *  newer-version seed, E-33). */
  apply(seed: string): void;
  /** The current forged skin as a seed string. */
  seed(): string;
  /** The reserved forge tile's RGBA bytes off the atlas (golden/same-tile proxy). */
  tilePixels(): number[];
}

/** The E2E contract for a multiplayer session (NET-2, M26.2/M27). */
export interface NetDebugApi {
  connected(): boolean;
  playerId(): number;
  /** Latest server-authoritative poses of every other connected player. `status`
   *  (§30.4, COW-5) is the data the nametag ring colour is driven from. */
  remotePlayers(): Array<{ id: number; name: string; hue: number; x: number; y: number; z: number; yaw: number; status?: string }>;
  /** Latest server-authoritative poses of every replicated mob (M28.1). */
  remoteEntities(): Array<{ id: number; type: string; x: number; y: number; z: number; yaw: number }>;
  sendChat(text: string): void;
  /** Run an op command against the server (`/summon …`, M28.1). */
  command(text: string): void;
  /** Request a server-arbitrated melee hit on an entity id (M28.1b). */
  attack(targetId: number): void;
  /** Predict a block edit locally and relay it for arbitration (NET-3). */
  edit(x: number, y: number, z: number, block: number | string): void;
  /** Chat lines received from the server, newest last (M27). */
  chatLog(): string[];
  /** Connected-player roster from the server (M27). `status` is the word the
   *  Tab-list shows beside a non-default presence (§30.4, COW-5). */
  roster(): Array<{ id: number; name: string; hue: number; status?: string }>;
  /** Largest reconciliation correction applied so far, in blocks (NET-3 smoothness). */
  maxCorrection(): number;
  /** Ask the server to board entity `vehicleId` (0 = dismount). The seat is chosen
   *  from the roster; the SERVER decides whether it is granted (mount replication). */
  mount(vehicleId: number): void;
  /** The authoritative seat roster: who is riding what, and in which seat. */
  riders(): Array<{ riderId: number; vehicleId: number; seat: number }>;
  /** Spectate a remote player id (chase cam), cycle to the next, or stop (NET C4). */
  spectate(id: number | null): void;
  spectateNext(): void;
  spectatingId(): number | null;
  /** The live camera world position (spectator/own view, for E2E) (NET C4). */
  cameraPos(): { x: number; y: number; z: number };
}

/** A pose snapshot returned by the replay API (player position + look). */
export interface ReplayPose { x: number; y: number; z: number; yaw: number; pitch: number }

/** The `voxel.replay` surface: record a canonical session, re-sim it, scrub, verify
 *  drift (E-31), export a timelapse, and query the live recorder (REP-1..5). */
export interface VoxelReplayApi {
  /** Begin a canonical recording anchored to the current world. `snapshot:true`
   *  starts from a live-world snapshot; otherwise from a fresh {seed, options}. */
  recordStart(opts?: { snapshot?: boolean }): string;
  /** Record one consumed input frame (fields default to a no-op frame). */
  recordStep(frame?: Partial<InputFrame>): void;
  /** Finish recording; the "live" final state + sizes. */
  recordFinish(): { frames: number; keyframes: number; liveHash: string; player: ReplayPose; bytes: number; jsonlLines: number };
  /** Re-sim the finished recording start→end (asserts every keyframe; throws on
   *  drift, E-31). Opens an internal player for `seek`. */
  replayVerify(): { finalHash: string; player: ReplayPose };
  /** Seek the active replay player to tick `t`; returns its state. */
  seek(t: number): { tick: number; hash: string; player: ReplayPose };
  /** Jump the active player to the keyframe at/just before its current tick. */
  seekKeyframe(): { tick: number; hash: string };
  /** Timelapse (REP-4): re-sim sampling a PNG every N ticks; returns frame count. */
  timelapse(everyN?: number): number;
  /** Instant-replay ring (REP-4): the last `REP_INSTANT_SECONDS` re-sims to the
   *  live final state → `true`. */
  instantVerify(): boolean;
  /** Round-trip the recording through the binary + JSONL codecs (REP-2) → `true`. */
  codecRoundTrip(): boolean;
  /** Reject a `.bbreplay` from a newer struct version, never crash (E-33) → `true`. */
  rejectsNewerVersion(): boolean;
  /** Whether the live (gamerule) recorder is running + the "● REC" dot is shown. */
  isRecording(): boolean;
  /** Open the live viewer overlay (F-key equivalent); `false` if nothing recorded. */
  openViewer(mode?: 'full' | 'instant'): boolean;
  /** 3D re-render state (REP-3 spectator): whether the viewer is drawing the replay
   *  world in 3D, the resident chunk-mesh count, and the spectator camera pose. */
  view3D(): { active: boolean; meshes: number; tick: number; camera: ReplayPose };
}

declare global {
  interface Window {
    voxel?: VoxelApi;
  }
}

function resolveState(game: Game, block: number | string): number {
  if (typeof block === 'number') return block;
  // Allow a full state key (e.g. "piston[facing=east,extended=false]") for E2E.
  const direct = game.registries.states.idByKey.get(block);
  if (direct !== undefined) return direct;
  const s = game.registries.states.idByKey.get(`${block}[axis=y]`) ?? game.registries.states.defaultOf.get(block);
  if (s === undefined) throw new Error(`unknown block '${block}'`);
  return s;
}

/** A §28.16 member whose milestone has not landed (see `HeredityDebugApi`). */
function notYet(milestone: string): never {
  throw new Error(`not yet (${milestone})`);
}

export function installDebugApi(game: Game): void {
  const isSolid = (x: number, y: number, z: number): boolean => game.world.isRaycastTarget(x, y, z);

  const lookDir = () => {
    const { yaw, pitch } = game.player;
    return { x: -Math.cos(pitch) * Math.sin(yaw), y: Math.sin(pitch), z: -Math.cos(pitch) * Math.cos(yaw) };
  };
  const eyePos = () => ({ x: game.player.body.x, y: game.player.body.y + game.eye, z: game.player.body.z });
  const cast = (maxDist = game.reach): RaycastHit | null => raycastVoxel(isSolid, eyePos(), lookDir(), maxDist);

  // ── Deterministic replay (M33, REP-1..5) ────────────────────────────────────
  // A self-contained record→re-sim→scrub→verify surface over the pure sim engine
  // (independent of the live gamerule recorder); `isRecording`/`openViewer` bridge
  // to the live ReplayController when one is wired.
  let repRecorder: Recorder | null = null;
  let repInit: SessionInit | null = null;
  let repReplay: Replay | null = null;
  let repPlayer: ReplayPlayer | null = null;

  const repSessionInit = (useSnapshot: boolean): SessionInit => {
    const p = game.player;
    const base: SessionInit = {
      seed: String(game.meta.seed),
      options: { flat: game.meta.worldType === 'flat', flatPreset: game.meta.flatPreset ?? '', dimension: 'overworld' },
      spawn: { x: p.body.x, y: p.body.y, z: p.body.z, yaw: p.yaw, pitch: p.pitch },
    };
    if (!useSnapshot) return base;
    const w = game.world, b = p.body;
    return {
      ...base,
      snapshot: {
        tickCount: w.tickCount, dayTime: w.dayTime,
        weather: { raining: w.weather.raining, thundering: w.weather.thundering, rainCountdown: w.weather.rainCountdown, thunderCountdown: w.weather.thunderCountdown },
        player: { x: b.x, y: b.y, z: b.z, vx: b.vx, vy: b.vy, vz: b.vz, yaw: p.yaw, pitch: p.pitch },
        loadedChunks: [...w.loadedChunks()].map((c) => ({ cx: c.cx, cz: c.cz })),
        sections: w.getDirtySections().map((s) => ({ cx: s.cx, cz: s.cz, sy: s.sy, states: s.states ? Array.from(s.states) : null })),
      },
    };
  };
  const fullFrame = (f: Partial<InputFrame> = {}): InputFrame => ({
    buttons: f.buttons ?? 0, yaw: f.yaw ?? 0, pitch: f.pitch ?? 0, flags: f.flags ?? 0,
    edits: f.edits ?? [], commands: f.commands ?? [],
  });
  const pose = (p: { x: number; y: number; z: number; yaw: number; pitch: number }): ReplayPose => ({ x: p.x, y: p.y, z: p.z, yaw: p.yaw, pitch: p.pitch });

  const replayApi: VoxelReplayApi = {
    recordStart: (opts) => {
      repInit = repSessionInit(opts?.snapshot ?? false);
      repRecorder = new Recorder(game.registries, repInit);
      repReplay = null; repPlayer = null;
      return repRecorder.session.hash();
    },
    recordStep: (frame) => { repRecorder!.record(fullFrame(frame)); },
    recordFinish: () => {
      repReplay = repRecorder!.finish();
      const liveHash = hashWorld(repRecorder!.session.world);
      const pp = repRecorder!.session.playerPose();
      const bytes = encodeReplay(repReplay).length;
      const jsonlLines = replayToJsonl(repReplay).split('\n').length;
      repRecorder = null;
      return { frames: repReplay.frames.length, keyframes: repReplay.keyframes.length, liveHash, player: pose(pp), bytes, jsonlLines };
    },
    replayVerify: () => {
      repPlayer = new ReplayPlayer(game.registries, repReplay!);
      const finalHash = repPlayer.resimVerify();
      return { finalHash, player: pose(repPlayer.playerPose()) };
    },
    seek: (t) => { repPlayer!.seek(t); return { tick: repPlayer!.tick, hash: repPlayer!.hash(), player: pose(repPlayer!.playerPose()) }; },
    seekKeyframe: () => { repPlayer!.seekToKeyframe(repPlayer!.tick - 1); return { tick: repPlayer!.tick, hash: repPlayer!.hash() }; },
    timelapse: (everyN = REP.KEYFRAME_TICKS) => {
      const player = new ReplayPlayer(game.registries, repReplay!);
      let n = 0;
      player.runTimelapse(everyN, () => { game.takeScreenshot(); n++; });
      return n;
    },
    instantVerify: () => {
      const ring = new InstantRing(game.registries, repInit!);
      for (const f of repReplay!.frames) ring.observe(f);
      return replayFinalHash(game.registries, ring.build()) === replayFinalHash(game.registries, repReplay!);
    },
    codecRoundTrip: () => {
      const truth = replayFinalHash(game.registries, repReplay!);
      const bin = replayFinalHash(game.registries, decodeReplay(encodeReplay(repReplay!)));
      const jsonl = replayFinalHash(game.registries, replayFromJsonl(replayToJsonl(repReplay!)));
      return bin === truth && jsonl === truth;
    },
    rejectsNewerVersion: () => {
      try { decodeReplay(encodeReplay({ ...repReplay!, version: REP.FORMAT_VERSION + 1 })); return false; }
      catch { return true; }
    },
    isRecording: () => game.replay?.recording ?? false,
    openViewer: (mode = 'instant') => (game.replay ? (mode === 'instant' ? game.replay.openInstantReplay() : game.replay.openViewer()) : false),
    view3D: () => game.replay?.view3D() ?? { active: false, meshes: 0, tick: -1, camera: { x: 0, y: 0, z: 0, yaw: 0, pitch: 0 } },
  };

  const api: VoxelApi = {
    game,
    player: game.player,
    getState: (x, y, z) => game.world.getBlockState(x, y, z),
    getBlock: (x, y, z) => game.registries.states.blockName[game.world.getBlockState(x, y, z)]!,
    props: (x, y, z) => game.blockPropsAt(x, y, z),
    setBlock: (x, y, z, block) => game.setBlock(x, y, z, resolveState(game, block)),
    tp: (x, y, z) => {
      const b = game.player.body;
      b.x = x; b.y = y; b.z = z;
      b.vx = b.vy = b.vz = 0;
      b.onGround = false;
    },
    look: (yaw, pitch) => {
      game.player.yaw = yaw;
      game.player.pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, pitch));
    },
    setInput: (partial) => Object.assign(game.input, partial),
    forceLock: (v) => { game.locked = v; },
    tickOnce: (n = 1) => { for (let i = 0; i < n; i++) game.tick(); },
    raycast: (maxDist) => cast(maxDist),
    mine: () => {
      const hit = cast();
      if (!hit) return false;
      game.mineBlock(hit.x, hit.y, hit.z);
      return true;
    },
    place: (block) => {
      const hit = cast();
      if (!hit) return false;
      const px = hit.x + hit.nx, py = hit.y + hit.ny, pz = hit.z + hit.nz;
      if (block !== undefined) {
        // Explicit block: raw place (test convenience, ignores inventory).
        if (game.world.getBlockState(px, py, pz) !== 0) return false;
        game.setBlock(px, py, pz, resolveState(game, block));
        return true;
      }
      return game.placeBlock(px, py, pz);
    },
    give: (item, count = 1) => game.give(item, count),
    mineAt: (x, y, z) => game.mineBlock(x, y, z),
    placeHeldAt: (x, y, z) => game.placeBlock(x, y, z),
    playSound: (id, x, y, z, pitch) => game.playSound(id, x, y, z, pitch),
    audioEvents: () => game.audioEvents().map((e) => ({ ...e })),
    lastAudioId: () => game.lastAudio()?.id ?? '',
    clearAudio: () => game.clearAudio(),
    resumeAudio: () => game.resumeAudio(),
    setAudioVolume: (bus, v) => game.setAudioVolume(bus, v),
    audioVolumes: () => game.audioVolumes(),
    insertDisc: (x, y, z, disc) => game.insertDisc(x, y, z, disc),
    jukeboxDisc: (x, y, z) => game.jukeboxDisc(x, y, z),
    discComparator: (x, y, z) => game.discComparator(x, y, z),
    musicContext: () => game.musicContext,
    setSubtitles: (on) => game.setSubtitles(on),
    subtitleLines: () => game.subtitleLines(),
    advancementsDone: () => game.advancementsDone(),
    advancementToasts: () => game.advancementToasts(),
    setKeybind: (action, code) => game.setKeybind(action, code),
    keybindFor: (action) => game.keybindFor(action),
    statCount: (category, id) => game.statCount(category, id),
    statCustom: (stat) => game.statCustom(stat),
    openStats: () => game.openStats(),
    screenshot: () => game.takeScreenshot(),
    screenshotCount: () => game.screenshotCount(),
    iconDataUrl: (item) => {
      const id = game.registries.items.idOf.get(item);
      return id === undefined ? '' : game.icons.get(id);
    },
    atlasDataUrl: () => game.atlasCanvas.toDataURL(),
    allItemNames: () => [...game.registries.items.byName.keys()],
    debugOverlayOn: () => game.debugOverlayOn(),
    chunkBordersOn: () => game.chunkBordersOn(),
    hitboxesOn: () => game.hitboxesOn(),
    recipeUnlockedCount: () => game.recipeBook.size,
    isRecipeUnlocked: (name) => game.recipeBook.isUnlocked(name),
    command: (text) => game.runCommand(text).message,
    inventory: () => game.playerState.inventory.slots.map((s) =>
      isEmpty(s) ? null : { name: game.registries.items.nameOf[s.item] ?? '?', count: s.count }),
    countItem: (item) => {
      const id = game.registries.items.idOf.get(item);
      return id === undefined ? 0 : game.playerState.inventory.countOf(id);
    },
    slotOf: (item) => {
      const id = game.registries.items.idOf.get(item);
      return id === undefined ? -1 : game.playerState.inventory.find(id);
    },
    select: (n) => { game.playerState.inventory.selected = Math.max(0, Math.min(8, n | 0)); },
    heldItem: () => {
      const held = game.playerState.inventory.held();
      return isEmpty(held) ? '' : game.registries.items.nameOf[held.item] ?? '';
    },
    openInventory: () => game.openInventory(),
    openTable: () => game.openCraftingTable(),
    openCreative: () => game.openCreative(),
    gameMode: () => game.playerState.gameMode,
    setGameMode: (mode) => game.setGameMode(mode),
    spawnMob: (type, x, y, z, opts) => game.entities.spawn(type, x, y, z, opts)?.id ?? -1,
    mobType: (id) => game.entities.entities.find((e) => e.id === id && !e.removed)?.type ?? null,
    mobCount: (type) => game.entities.entities.filter((e) => !type || e.type === type).length,
    mobs: () => game.entities.entities.map((e) => ({ id: e.id, type: e.type, x: e.x, y: e.y, z: e.z, health: e.health, baby: e.baby, fuse: e.fuse, tamed: e.tamed, sitting: e.sitting, sheared: e.sheared, variant: e.variant })),
    nearestMob: (type) => {
      const b = game.player.body;
      let best: (typeof game.entities.entities)[number] | null = null;
      let bestD = Infinity;
      for (const e of game.entities.entities) {
        if (type && e.type !== type) continue;
        const d = (e.x - b.x) ** 2 + (e.y - b.y) ** 2 + (e.z - b.z) ** 2;
        if (d < bestD) { bestD = d; best = e; }
      }
      return best ? { id: best.id, type: best.type, x: best.x, y: best.y, z: best.z, health: best.health, dist: Math.sqrt(bestD), tamed: best.tamed, sitting: best.sitting, sheared: best.sheared } : null;
    },
    attack: () => game.attack(),
    useEntity: () => game.useEntity(),
    villagerOffers: (id) => game.villagerOffers(id),
    doTrade: (id, index) => game.doTrade(id, index),
    villagerActivity: (id) => game.villagerActivity(id),
    villagerReputation: (id) => game.villagerReputation(id),
    addVillagerGossip: (id, type, amount) => game.addVillagerGossip(id, type, amount),
    shareVillagerGossip: () => game.shareVillagerGossip(),
    decayVillagerGossip: () => game.decayVillagerGossip(),
    weakenMob: (id) => game.weakenMob(id),
    interactMob: (id) => game.interactMob(id),
    finishCure: (id) => game.finishCure(id),
    frozenTicks: () => game.frozenTicks,
    hiveHoney: (x, y, z) => game.hiveHoney(x, y, z),
    pollinateHive: (x, y, z) => game.pollinateHive(x, y, z),
    frogEat: (id) => game.frogEat(id),
    layEgg: (id) => game.layEgg(id),
    forceSpawnCycle: () => game.forceSpawnCycle(),
    villagerWilling: (id) => game.villagerWilling(id),
    tryBreedVillagers: () => game.tryBreedVillagers(),
    villagerBed: (id) => game.villagerBed(id),
    assignVillagerJob: (id) => game.assignVillagerJob(id),
    trySpawnIronGolem: () => game.trySpawnIronGolem(),
    forcePatrol: () => game.forcePatrol(),
    ringBell: (x, y, z) => game.ringBell(x, y, z),
    summonVexes: (id) => game.summonVexes(id),
    fireDispenser: (x, y, z) => game.fireDispenser(x, y, z),
    loadBrewingStand: (x, y, z, bottles, ingredient, fuel) => game.loadBrewingStand(x, y, z, bottles, ingredient, fuel),
    finishBrewing: (x, y, z) => game.finishBrewing(x, y, z),
    brewingStandBottles: (x, y, z) => game.brewingStandBottles(x, y, z),
    effectTint: () => game.effectTint,
    nightVisionFloor: () => game.nightVisionFloor,
    setSignText: (x, y, z, lines) => game.setSignText(x, y, z, lines),
    signText: (x, y, z) => game.signText(x, y, z),
    signGlows: (x, y, z) => game.signGlows(x, y, z),
    signPixels: (x, y, z) => game.signPixels(x, y, z),
    signBoardSize: () => game.signBoardSize(),
    signTextureLitCount: (x, y, z) => game.signTextureLitCount(x, y, z),
    noteBlockNote: (x, y, z) => game.noteBlockNote(x, y, z),
    cycleNoteBlock: (x, y, z) => game.cycleNoteBlock(x, y, z),
    playNoteBlock: (x, y, z) => game.playNoteBlock(x, y, z),
    lastNotePlayed: () => game.lastNotePlayed,
    setCrafterSlot: (x, y, z, i, item, count) => game.setCrafterSlot(x, y, z, i, item, count),
    toggleCrafterSlot: (x, y, z, i) => game.toggleCrafterSlot(x, y, z, i),
    crafterSlots: (x, y, z) => game.crafterSlots(x, y, z),
    fireCrafter: (x, y, z) => game.fireCrafter(x, y, z),
    lastCrafted: () => game.lastCrafted,
    reloadEntitiesRoundTrip: () => game.reloadEntitiesRoundTrip(),
    raid: () => game.raidInfo(),
    forceRaid: () => game.forceRaid(),
    raidState: () => game.raidState(),
    restoreRaid: (state) => game.restoreRaid(state),
    hurtMob: (id, amount) => game.hurtMob(id, amount),
    hurtPlayer: (amount, fromX, fromZ) => game.hurtPlayer(amount, fromX, fromZ),
    equip: (item) => game.equip(item),
    glide: () => { game.glide(); },
    gliding: () => game.gliding,
    useFirework: () => game.useFirework(),
    fishCatch: () => game.fishCatch(),
    createFilledMap: (scale) => game.createFilledMap(scale),
    createTreasureMap: (scale) => game.createTreasureMap(scale),
    heldMapId: () => {
      const inv = game.playerState.inventory;
      for (const stack of [inv.held(), inv.get(INV.OFFHAND)]) {
        if (isEmpty(stack)) continue;
        if (game.registries.items.nameOf[stack.item] !== 'filled_map') continue;
        const id = stack.components?.mapId;
        if (id !== undefined) return id;
      }
      return -1;
    },
    mapCount: () => game.mapStore.size,
    refreshMap: (mapId, radius) => game.refreshMap(mapId, radius),
    mapPixel: (mapId, u, v) => game.mapStore.get(mapId)?.pixelAt(u, v) ?? null,
    mapMappedCount: (mapId) => game.mapStore.get(mapId)?.mappedCount() ?? 0,
    mapMarker: (mapId) => game.mapStore.get(mapId)?.marker(game.player.body.x, game.player.body.z, game.player.yaw) ?? null,
    mapTarget: (mapId, tx, tz) => game.mapStore.get(mapId)?.targetMarker(tx, tz) ?? null,
    growAmethyst: (x, y, z, dirIndex) => game.growAmethyst(x, y, z, dirIndex),
    bloomSculk: (x, y, z, charge) => game.bloomSculk(x, y, z, charge),
    beaconTier: (x, y, z) => game.beaconTier(x, y, z),
    setBeaconEffect: (x, y, z, primary, secondary) => game.setBeaconEffect(x, y, z, primary, secondary),
    pulseBeacon: (x, y, z) => game.pulseBeacon(x, y, z),
    summonWither: (x, y, z) => game.summonWither(x, y, z),
    conduitFrame: (x, y, z) => game.conduitFrame(x, y, z),
    pulseConduit: (x, y, z) => game.pulseConduit(x, y, z),
    shriek: (x, y, z) => game.shriek(x, y, z),
    wardenSonicBoom: (id) => game.wardenSonicBoom(id),
    dragonCrystals: (id) => game.dragonCrystals(id),
    dragonPhase: (id) => game.dragonPhase(id),
    trialWave: () => game.trialWave(),
    trialCap: () => game.trialCap(),
    forceTrialEject: (x, y, z) => game.forceTrialEject(x, y, z),
    growDripstone: (x, y, z) => game.growDripstone(x, y, z),
    useAnchor: (x, y, z) => game.useAnchor(x, y, z),
    pulseOceanFauna: () => game.pulseOceanFauna(),
    pulseGuardians: () => game.pulseGuardians(),
    zombifyHoglin: (id) => game.zombifyHoglin(id),
    turtleEggTick: (x, y, z) => game.turtleEggTick(x, y, z),
    sculkVibrate: (x, y, z) => game.sculkVibrate(x, y, z),
    windCharge: (x, y, z) => game.windCharge(x, y, z),
    brushBlock: (x, y, z) => game.brushBlock(x, y, z),
    snifferDig: (id) => game.snifferDig(id),
    camelDash: (id) => game.camelDash(id),
    growTadpole: (id) => game.growTadpole(id),
    oxidizeCopper: (x, y, z) => game.oxidizeCopper(x, y, z),
    campfireCount: (x, y, z) => game.campfireCount(x, y, z),
    cookCampfire: (x, y, z) => game.cookCampfire(x, y, z),
    rainCauldron: (x, y, z) => game.rainCauldron(x, y, z),
    growPlant: (x, y, z) => game.growPlant(x, y, z),
    randomTick: (x, y, z) => game.randomTick(x, y, z),
    placeHeld: (x, y, z) => game.placeHeld(x, y, z),
    redstone: (x, y, z) => game.redstoneAt(x, y, z),
    useBlock: (x, y, z) => game.useBlock(x, y, z),
    blockEntityType: (x, y, z) => game.blockEntities.get(x, y, z)?.type ?? null,
    putContainer: (x, y, z, slot, item, count) => {
      const id = game.registries.items.idOf.get(item);
      if (id === undefined) return false;
      const name = game.registries.states.blockName[game.world.getBlockState(x, y, z)];
      const beType = name ? game.registries.blocks.byName.get(name)?.blockEntity : undefined;
      if (!beType) return false;
      const be = game.blockEntities.create(beType, x, y, z);
      const stack = makeStack(id, count);
      if (be.type === 'chest') be.data.items[slot] = stack;
      else if (be.type === 'furnace') {
        if (slot === 0) be.data.input = stack;
        else if (slot === 1) be.data.fuel = stack;
        else be.data.output = stack;
      } else if (be.type === 'hopper' || be.type === 'dropper' || be.type === 'dispenser') {
        if (slot < 0 || slot >= be.data.items.length) return false;
        be.data.items[slot] = stack;
      } else return false;
      return true;
    },
    containerItems: (x, y, z) => {
      const be = game.blockEntities.get(x, y, z);
      if (!be) return [];
      const stacks = be.type === 'chest' ? chestContents(be.data)
        : be.type === 'furnace' ? furnaceContents(be.data)
        : be.type === 'hopper' || be.type === 'dropper' || be.type === 'dispenser' ? arrayContents(be.data)
        : [];
      return stacks.map((s) => {
        const mapId = s.components?.mapId;
        const out: { name: string; count: number; mapId?: number } = {
          name: game.registries.items.nameOf[s.item] ?? '?', count: s.count,
        };
        if (typeof mapId === 'number') out.mapId = mapId;
        return out;
      });
    },
    realizeChest: (x, y, z) => {
      const be = game.blockEntities.get(x, y, z);
      if (be?.type !== 'chest') return 0;
      fillChestLoot(be.data, game.registries, { explorerMap: (d) => game.explorerMap(d) });
      return chestContents(be.data).length;
    },
    seedLootChest: (x, y, z, table, seed = 1) => {
      const be = game.blockEntities.get(x, y, z) ?? game.blockEntities.create('chest', x, y, z);
      if (be.type !== 'chest') return false;
      be.data.loot = table;
      be.data.lootSeed = seed;
      return true;
    },
    mapPin: (mapId) => {
      const t = game.mapStore.get(mapId)?.target;
      return t ? { x: t.x, z: t.z } : null;
    },
    locate: (type) => {
      const b = game.player.body;
      return game.cmdContext.locate(type, b.x, b.z);
    },
    spawnerMob: (x, y, z) => {
      const be = game.blockEntities.get(x, y, z);
      return be?.type === 'spawner' ? be.data.mob ?? null : null;
    },
    vibrate: (event, x, y, z, source = 0) => game.emitVibration(event, x, y, z, source),
    mountState: (id) => {
      const e = game.entities.entities.find((m) => m.id === id && !m.removed);
      if (!e) return null;
      return {
        speedMul: e.data.speedMul ?? 1,
        jumpMul: e.data.jumpMul ?? 1,
        jumpCharge: e.data.jumpCharge ?? 0,
        dashCd: e.data.dashCd ?? 0,
        saddled: e.data.saddled === 1,
      };
    },
    wardenAnger: (wardenId, suspect) => game.wardenAngerAt(wardenId, suspect),
    wardenQuarry: (wardenId) => game.wardenQuarryOf(wardenId),
    closeUI: () => {
      document.dispatchEvent(new KeyboardEvent('keydown', { code: 'Escape', bubbles: true }));
    },
    get orbCount() { return game.entities.orbs.length; },
    get projectileCount() { return game.entities.projectiles.length; },
    get health() { return game.playerState.health; },
    get hunger() { return game.playerState.hunger; },
    get xpLevel() { return game.playerState.xpLevel; },
    get xpTotal() { return game.playerState.xpTotal; },
    get flying() { return game.player.body.flying ?? false; },
    get dropCount() { return game.drops.count; },
    get fallingCount() { return game.fallingBlocks.count; },
    get primedTntCount() { return game.primedTnt.count; },
    ignitePrimedTnt: (x, y, z) => game.ignitePrimedTntAt(x, y, z),
    get minecartCount() { return game.minecarts.count; },
    spawnMinecart: (x, y, z, dir = 'north', speed = 0, kind = 'minecart') => {
      game.minecarts.spawn(x, y, z, dir, speed, kind as Parameters<typeof game.minecarts.spawn>[5]);
    },
    fuelMinecart: (i, ticks = 1200) => { const c = game.minecarts.carts[i]; if (c) game.minecarts.fuelFurnace(c, ticks); },
    dropItem: (item, x, y, z, count = 1) => {
      const id = game.registries.items.idOf.get(item);
      if (id !== undefined) game.drops.spawn(makeStack(id, count), x, y, z, { pickupDelay: 0 });
    },
    get boatCount() { return game.boats.count; },
    spawnBoat: (x, y, z, yaw = 0, kind = 'boat') => game.boats.spawn(x, y, z, yaw, kind as Parameters<typeof game.boats.spawn>[4]).id,
    setBoatInput: (id, input) => game.setBoatInput(id, input),
    boatAt: (i) => {
      const b = game.boats.boats[i];
      return b ? { id: b.id, x: b.x, y: b.y, z: b.z, yaw: b.yaw, onWater: b.onWater, kind: b.kind, items: b.items?.filter(Boolean).length ?? 0 } : null;
    },
    mountBoat: (id) => game.mountBoat(id),
    dismountBoat: () => game.dismountBoat(),
    ridingBoatId: () => game.ridingBoatId(),
    mountEntity: (id) => game.mountEntity(id),
    dismountMount: () => game.dismountMount(),
    ridingMount: () => game.ridingMount(),
    saddleMount: (id) => game.saddleMount(id),
    get aircraftCount() { return game.aircraft.count; },
    enterAircraft: () => game.enterAircraft(),
    exitAircraft: () => game.exitAircraft(),
    aircraft: () => {
      const a = game.flyingAircraft();
      if (!a) return null;
      return {
        x: a.x, y: a.y, z: a.z, yaw: a.yaw, pitch: a.pitch, roll: a.roll,
        throttle: a.throttle, onGround: a.onGround, airborne: !a.onGround, crashed: a.crashed,
        heading: ((a.yaw * 180 / Math.PI) % 360 + 360) % 360,
        airspeed: Math.hypot(a.vx, a.vy, a.vz) * 20,
        altitude: a.y,
      };
    },
    setAircraftInput: (input) => game.setAircraftInput(input),
    setAutopilot: (targets) => game.setAutopilot(targets && { altitude: targets.altitude, heading: targets.heading ?? null, throttle: targets.throttle ?? 0.6 }),
    autopilotEngaged: () => game.autopilotEngaged(),
    sar: {
      deploy: (x, y, z) => game.deployBeacon(x, y, z),
      remove: (id) => game.removeBeacon(id),
      clear: () => game.clearBeacons(),
      beacons: () => game.beaconList(),
      signal: (x, y, z) => game.sarSignal(x, y, z),
      bearing: (x, y, z, yaw) => game.sarBearing(x, y, z, yaw),
      objective: (searcher) => game.sarObjectiveState(searcher),
      hud: (v) => game.toggleSarHud(v),
    },
    heli: {
      get count() { return game.helicopters.count; },
      spawn: (x, y, z, yaw = 0) => game.spawnHeli(x, y, z, yaw),
      enter: (id) => game.enterHeli(id),
      exit: () => game.exitHeli(),
      board: (id) => game.boardHeli(id),
      input: (i) => game.setHeliInput(i),
      at: (i) => { const h = game.helicopters.helicopters[i]; return h ? { id: h.id, x: h.x, y: h.y, z: h.z, yaw: h.yaw, vy: h.vy, onGround: h.onGround, crashed: h.crashed, altitude: h.y } : null; },
      riders: (id) => game.heliRiderCount(id),
      piloted: () => game.pilotedHeliId(),
    },
    van: {
      get count() { return game.vans.count; },
      spawn: (x, y, z, yaw = 0) => game.spawnVan(x, y, z, yaw),
      enter: (id) => game.enterVan(id),
      exit: () => game.exitVan(),
      board: (id) => game.boardVan(id),
      input: (i) => game.setVanInput(i),
      at: (i) => { const v = game.vans.vans[i]; return v ? { id: v.id, x: v.x, y: v.y, z: v.z, yaw: v.yaw, speed: v.speed } : null; },
      riders: (id) => game.vanRiderCount(id),
      piloted: () => game.pilotedVanId(),
    },
    classroom: {
      aim: () => game.blueprintAim(),
      place: (dsl, x, y, z, rot) => game.placeBlueprint(dsl, x, y, z, rot),
      course: (entries) => game.setBlueprintCourse(entries),
      clear: () => game.clearBlueprint(),
      state: () => game.blueprintState(),
      heat: (v) => game.toggleClassroomHeat(v),
      done: () => game.blueprintState()?.done ?? [],
    },
    minecartAt: (i) => {
      const c = game.minecarts.carts[i];
      return c ? { x: c.x, y: c.y, z: c.z, cx: c.cx, cy: c.cy, cz: c.cz, dir: c.dir, speed: c.speed, kind: c.kind, fuse: c.fuse ?? 0, items: c.items?.filter(Boolean).length ?? 0 } : null;
    },
    get dead() { return game.playerState.dead; },
    get dimension() { return game.world.dimension; },
    get simulated() { return game.meta.atlas?.simulated ?? false; },
    ignitePortal: (x, y, z) => game.ignitePortalAt(x, y, z),
    isPortal: (x, y, z) => game.registries.states.blockName[game.world.getBlockState(x, y, z)] === 'nether_portal',
    get minimapRadius() { return game.minimap?.radius ?? 0; },
    cycleMinimapZoom: () => game.minimap?.cycleZoom() ?? 0,
    minimapClasses: () => {
      const mm = game.minimap;
      const classes = new Set<string>();
      if (!mm) return [];
      const r = mm.radius, px = Math.floor(game.player.body.x), pz = Math.floor(game.player.body.z);
      for (let dz = -r; dz <= r; dz += 2) for (let dx = -r; dx <= r; dx += 2) {
        const rgb = mm.colorAt(px + dx, pz + dz);
        if (!rgb) continue;
        const [rr, gg, bb] = rgb;
        if (rr > 200 && gg > 200 && bb > 200) classes.add('white');
        else if (bb > rr + 30 && bb > gg) classes.add('blue');
        else if (gg > rr + 20 && gg > bb + 10) classes.add('green');
        else if (rr > gg + 25 && rr > bb + 25) classes.add('brown');
        else if (Math.abs(rr - gg) < 24 && Math.abs(gg - bb) < 24) classes.add('gray');
      }
      return [...classes];
    },
    fluidLevel: (x, y, z) => game.world.fluidLevelOf(game.world.getBlockState(x, y, z)),
    useBucket: () => game.useBucket(),
    useHeldItem: () => game.useHeldItem(0, 0, 0),
    useFieldNotebook: () => game.useFieldNotebook(),
    useItemOnBlock: (x, y, z, ny = 1) => game.useItemOnBlock(x, y, z, 0, ny, 0),
    useItemOnFace: (x, y, z, nx, ny, nz) => game.useItemOnBlock(x, y, z, nx, ny, nz),
    prop: (x, y, z, name) => game.registries.states.props[game.world.getBlockState(x, y, z)]?.[name],
    enchantOffers: (x, y, z) => game.enchantOffers(x, y, z),
    enchant: (x, y, z, slot) => game.applyEnchant(x, y, z, slot),
    heldEnchants: () => game.playerState.inventory.held().components?.enchants ?? [],
    effects: () => game.playerState.effects.map((e) => ({ ...e })),
    giveEffect: (id, amplifier = 0, ticks = 600) => addEffect(game.playerState.effects, id, amplifier, ticks),
    throwSplash: (effect = 'poison', amplifier = 0, seconds = 45) => {
      const b = game.player.body;
      // Drop it from just above the player's head so it shatters on them next tick.
      game.entities.spawnProjectile('potion', b.x, b.y + 2.5, b.z, 0, -0.3, 0, -1, 0, {
        id: effect, amplifier, ticks: Math.round(seconds * 20),
      });
    },
    get raining() { return game.world.weather.raining; },
    get thundering() { return game.world.weather.thundering; },
    get timeSinceRest() { return game.playerState.timeSinceRest; },
    setWeather: (kind, seconds) => setWeather(game.world.weather, kind, seconds !== undefined ? seconds * 20 : undefined),
    skyLight: (x, y, z) => game.world.getSkyLight(x, y, z),
    blockLight: (x, y, z) => game.world.getBlockLight(x, y, z),
    setTime: (t) => { game.world.dayTime = t; },
    get daylight() { return game.world.skyState().daylight; },
    get sunElevation() { return game.world.skyState().sun.elevation; },
    get skyReal() { return game.world.skyState().real; },
    save: () => game.saveNow(),
    exportWorld: () => exportWorldFile(game.save, game.meta.id),
    get fps() { return game.fps; },
    get chunkCount() { return game.world.loadedCount; },
    get worstFrameMs() { return game.worstFrameMs; },
    get meshRebuilds() { return game.chunkMeshes.rebuildCount; },
    resetFrameStats: () => { game.worstFrameMs = 0; },
    replay: replayApi,
    forge: {
      open: () => game.openForge(),
      apply: (seed) => game.applyForge(unpackSeed(seed)),
      seed: () => game.forgeSeed(),
      tilePixels: () => game.forgeTilePixels(),
    },
    heredity: {
      rule: (on) => game.heredityRule(on),
      pace: (n) => game.heredityPace(n),
      genome: (id) => game.heredityGenome(id),
      setGenome: (id, b64) => game.setHeredityGenome(id, b64),
      traits: (id) => game.heredityTraits(id),
      breed: () => notYet('M48.5'),
      ageTo: (id, ticks) => game.ageEntityTo(id, ticks),
      name: (id) => game.entities.byId(id)?.name ?? null,
      setName: (id, text) => game.setEntityName(id, text),
      entityNametag: (id) => game.entityNametag(id),
      cropGenome: (x, y, z) => game.cropGenome(x, y, z),
      plantGenetic: (x, y, z, crop, b64) => game.plantGenetic(x, y, z, crop, b64),
      harvest: (x, y, z) => game.harvest(x, y, z),
      record: (id) => game.recordAnimalById(id),
      recordCrop: (x, y, z) => game.recordCropAt(x, y, z),
      notebook: () => game.notebook,
      overlayCount: () => game.overlayCount(),
      entityTint: (id, part) => game.entityTint(id, part),
      entityPartVisible: (id, part) => game.entityPartVisible(id, part),
      entityScale: (id) => game.entityScale(id),
      lastActionBar: () => game.lastActionBar(),
    },
    edu: {
      lessons: () => game.eduLessons(),
      startLesson: () => notYet('M52'),
      stopLesson: () => notYet('M52'),
      activeLesson: () => game.eduActiveLesson(),
      result: () => game.eduResult() ?? null,
      fire: (t) => game.eduFire(t),
      readInstrument: (item, x, y, z) => game.readInstrument(item, x, y, z),
      submitQuiz: (answers) => game.eduSubmitQuiz(answers),
      raiseHand: (up) => game.eduRaiseHand(up),
      frozen: () => game.eduControl.frozen || game.eduControl.eyesUp !== null,
      roster: () => game.eduRoster(),
      exportJson: () => game.eduLastExport(),
      lastActionBar: () => game.lastActionBar(),
    },
    cow: {
      status: (v) => game.cowStatus(v),
      noteText: (x, y, z) => game.cowNoteText(x, y, z),
      setNote: (x, y, z, text) => game.cowSetNote(x, y, z, text),
      board: (x, y, z) => game.cowBoard(x, y, z),
      toggleTask: (x, y, z, i) => game.cowToggleTask(x, y, z, i),
      timer: (x, y, z) => game.cowTimer(x, y, z),
      timerFace: (x, y, z) => game.cowTimerFace(x, y, z),
      startTimer: (x, y, z, preset) => game.cowStartTimer(x, y, z, preset),
      screenState: (x, y, z) => game.cowScreenState(x, y, z),
      canShare: () => game.cowCanShare(),
      viewers: (x, y, z) => game.cowViewers(x, y, z),
      viewerStates: (x, y, z) => game.cowViewerStates(x, y, z),
      fakeShare: (x, y, z) => game.cowFakeShare(x, y, z),
      forceWatchFail: () => game.cowForceWatchFail(),
      signText: (x, y, z) => game.signText(x, y, z),
      lastActionBar: () => game.lastActionBar(),
    },
    script: {
      source: (x, y, z) => game.scriptAt(x, y, z)?.source ?? null,
      set: (x, y, z, source) => game.setScriptSource(x, y, z, source),
      run: (x, y, z, event) => game.runScriptBlock(x, y, z, event),
      setAutopilot: (x, y, z, preset) => game.setScriptAutopilot(x, y, z, preset),
      engage: (x, y, z) => game.engageScriptAutopilot(x, y, z),
    },
    earth: {
      loadDefault: () => {
        const set = loadBundledFixture(); // bundled KHFD tiles → zero network (ERT-6)
        const dem0 = set.dem[0];
        // Sample the decoded DEM at the KHFD point (the tile spans ~9.7 km; its centre
        // is beyond the filled world window, so sample where the data lives).
        let sampleElev = 0;
        if (dem0) {
          const grid = decodeDemData(dem0.data);
          const col = Math.round((ATLAS.DEFAULT.lon - dem0.origin.lon) / dem0.cellDeg.dLon);
          const row = Math.round((dem0.origin.lat - ATLAS.DEFAULT.lat) / dem0.cellDeg.dLat);
          const idx = row * dem0.w + col;
          if (idx >= 0 && idx < grid.length) sampleElev = grid[idx]!;
        }
        return {
          source: set.source,
          contractMajor: set.meta.contract.major,
          demTiles: set.dem.length,
          vectorTiles: set.vector.length,
          demSource: dem0?.source ?? '',
          demW: dem0?.w ?? 0,
          sampleElev,
          roads: set.vector.reduce((n, t) => n + t.roads.length, 0),
          aeroway: set.vector.reduce((n, t) => n + t.aeroway.length, 0),
          attribution: set.meta.attribution,
        };
      },
    },
    overlay: {
      enabled: () => !!game.overlay,
      add: (dataUrl, pxW, pxH) => game.overlay ? game.overlay.add(dataUrl, pxW, pxH) : Promise.resolve({ ok: false, reason: 'not an Earth world' }),
      list: () => game.overlay?.list() ?? [],
      count: () => game.overlay?.count() ?? 0,
      nudge: (id, dx, dy, dz) => game.overlay?.nudge(id, dx, dy, dz),
      setOpacity: (id, o) => game.overlay?.setOpacity(id, o),
      toggleHidden: (id) => game.overlay?.toggleHidden(id),
      remove: (id) => game.overlay?.remove(id),
      worldHash: () => hashWorld(game.world),
      bake: () => game.bakeImagery(),
      unbake: () => game.unbakeImagery(),
      baked: () => game.hasBakedImagery(),
    },
    door: {
      at: (x, y, z) => game.worldDoorAt(x, y, z),
      createChild: (x, y, z, opts) => game.createChildThroughDoor(x, y, z, { worldType: opts.worldType as ChildOpts['worldType'], size: opts.size ?? 0, name: opts.name, public: opts.public }),
      enter: (x, y, z) => game.traverseDoor(x, y, z),
      resize: (x, y, z, size) => game.resizeDoorTarget(x, y, z, size),
      worldId: () => game.meta.id,
      arrivalDoor: () => game.doorArrival ?? null,
      returnHome: () => game.returnHome(),
      usage: async () => { const r = await game.storageUsage(); return { worlds: r.worlds, totalBytes: r.totalBytes, quotaBytes: r.quotaBytes, fraction: r.fraction, warn: r.warn }; },
      deleteWorld: (id) => {
        // Net (M42): delete the SERVER world (drives the E-34 inert-door path); solo hits IDB.
        const n = game.net;
        if (n?.ctl && n.guest) return n.ctl.deleteServerWorld(n.controlOrigin, n.guest, id);
        return game.save.deleteWorld(id);
      },
    },
    streaming: {
      lod: () => game.streamingDebug().lod,
      renderCap: () => game.streamingDebug().renderCap,
      meshWorkers: () => game.streamingDebug().meshWorkers,
      genWorkers: () => game.streamingDebug().genWorkers,
      loadedChunks: () => game.streamingDebug().loadedChunks,
      heading: () => { const s = game.streamingDebug(); return { vx: s.vx, vz: s.vz }; },
    },
    net: game.net && {
      connected: () => game.net!.connected(),
      playerId: () => game.net!.welcome.playerId,
      remotePlayers: () => game.net!.remotePlayers().map((p) => ({ id: p.id, name: p.name, hue: p.hue, x: p.x, y: p.y, z: p.z, yaw: p.yaw, status: p.status })),
      remoteEntities: () => game.net!.remoteEntities().map((e) => ({ id: e.id, type: e.type, x: e.x, y: e.y, z: e.z, yaw: e.yaw })),
      sendChat: (text) => game.net!.sendChat(text),
      command: (text) => game.net!.sendCommand(text),
      attack: (targetId) => {
        const held = game.playerState.inventory.held(); // send the held item (weapon-aware, C5)
        game.net!.sendAttack(targetId, isEmpty(held) ? 0 : held.item);
      },
      mount: (vehicleId) => game.requestMount(vehicleId),
      riders: () => game.net!.riders.map((r) => ({ ...r })),
      spectate: (id) => game.spectate(id),
      spectateNext: () => game.spectateNext(),
      spectatingId: () => game.spectatingId(),
      cameraPos: () => ({ x: game.camera.position.x, y: game.camera.position.y, z: game.camera.position.z }),
      edit: (x, y, z, block) => {
        const s = resolveState(game, block);
        game.setBlock(x, y, z, s); // local prediction (applies via edit(), no re-send)
        game.net!.sendEdit(x, y, z, s, s !== 0);
      },
      chatLog: () => [...game.net!.chat],
      roster: () => game.net!.players(),
      maxCorrection: () => game.net!.maxCorrection,
    },
  };

  window.voxel = api;
}
